import api, { API } from "@/lib/api";

export const SPEECH_LANG = { en: "en-US", ta: "ta-IN", hi: "hi-IN", te: "te-IN", ml: "ml-IN" };

const clean = (t) => String(t || "").replace(/https?:\/\/\S+/g, "").replace(/[*_#>`~|]/g, "").replace(/\s+/g, " ").trim();

const state = { queue: [], busy: false, audio: null, spoken: new Map(), listeners: new Set(), current: null };
const SPOKEN_TTL = 5 * 60 * 1000;

const emit = () => state.listeners.forEach((fn) => fn({ speaking: state.busy, current: state.current }));

function pickVoice(lang) {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  const base = lang.split("-")[0];
  return voices.find((v) => v.lang === lang) || voices.find((v) => v.lang?.toLowerCase().startsWith(base)) || null;
}

function speakOne(text, lang) {
  return new Promise((resolve) => {
    const t = clean(text);
    if (!t) return resolve();
    const voice = pickVoice(lang);
    const useBrowser = "speechSynthesis" in window && (lang.startsWith("en") || voice);
    if (useBrowser) {
      if (window.speechSynthesis.speaking || window.speechSynthesis.pending) window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(t);
      u.lang = lang; if (voice) u.voice = voice; u.rate = 1;
      let done = false;
      const finish = () => { if (!done) { done = true; resolve(); } };
      u.onend = finish; u.onerror = finish;
      window.speechSynthesis.speak(u);
      setTimeout(finish, Math.min(120000, 4000 + t.length * 90));
      return;
    }
    api.post("/broker/voice/tts", { text: t }).then(({ data }) => {
      state.audio?.pause();
      const a = new Audio(`${API.replace(/\/api$/, "")}${data.url}`);
      let done = false;
      const finish = () => { if (!done) { done = true; resolve(); } };
      state.audio = a; a.onended = finish; a.onerror = finish;
      a.onloadedmetadata = () => setTimeout(finish, Math.min(180000, (a.duration || 60) * 1000 + 5000));
      setTimeout(finish, 180000);
      a.play().catch(finish);
    }).catch(resolve);
  });
}

async function drain() {
  if (state.busy) return;
  state.busy = true; emit();
  while (state.queue.length) {
    const item = state.queue.shift();
    state.current = item.key || null; emit();
    await speakOne(item.text, item.lang);
  }
  state.busy = false; state.current = null; emit();
}

export const speech = {
  supported: typeof window !== "undefined" && "speechSynthesis" in window,
  langFor: (code) => SPEECH_LANG[code] || "en-US",
  wasSpoken(key) {
    const at = key ? state.spoken.get(key) : undefined;
    return at !== undefined && Date.now() - at < SPOKEN_TTL;
  },
  markSpoken(key) { if (key) state.spoken.set(key, Date.now()); },
  speak(text, { lang = "en-US", key = null, urgent = false, force = false } = {}) {
    if (!text) return false;
    if (key && !force && speech.wasSpoken(key)) return false;
    if (key) speech.markSpoken(key);
    if (urgent) speech.cancel();
    else state.queue = state.queue.filter((q) => !key || q.key !== key);
    state.queue.push({ text, lang, key });
    drain();
    return true;
  },
  cancel() {
    state.queue = [];
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    state.audio?.pause();
    state.audio = null;
  },
  subscribe(fn) { state.listeners.add(fn); fn({ speaking: state.busy, current: state.current }); return () => state.listeners.delete(fn); },
};

if (typeof window !== "undefined") window.speechSynthesis?.getVoices?.();
